import { useTranslate } from "helpers";
import { VehicleContractSaleInfoProps } from "./types";
import { memo, useCallback } from "react";
import { TextField } from "ui/components/TextField/TextField";
import {
  useHandleDateChange,
  useHandleDropdownChange,
  useHandleNumberFieldChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";
import { DatePicker } from "ui/components/DatePicker";
import VehicleDropdown from "ui/components/Dropdown/VehicleDropdown/VehicleDropdown";
import FleetOfferSaleDropdown from "ui/components/Dropdown/FleetOfferSaleDropdown/FleetOfferSaleDropdown";
import { FleetVehicleOfferSaleRow } from "services/api/fleet/vehicles/offers/types";
import { getContactRelationsAPI, getContactPhonesAPI } from "services/api/contacts/api";
import { initialDataWithPagination } from "services/api/consts";
import { NumberField } from "ui/components/TextField/NumberField";
import ContractSellerCard from "./components/ContractSellerCard/ContractSellerCard";
import ContractBuyerCard from "./components/ContractBuyerCard/ContractBuyerCard";


const VehicleContractSaleInfo: React.FC<VehicleContractSaleInfoProps> = (props) => {
  const { vehicleId, formValues, setFormValues, errors, setErrors, onOfferSalesLoaded } = props;

  const { t } = useTranslate();

  const handleNumberFieldChange = useHandleNumberFieldChange(
    setFormValues,
    formValues
  );

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues
  );

  const handleDateChange = useHandleDateChange(setFormValues, formValues);

  const handleOfferSelect = useCallback(async (offer: FleetVehicleOfferSaleRow | null) => {
    if (!offer) {
      setFormValues(prev => ({ ...prev, offerSale: null }));
      return;
    }

    setErrors({});

    const contact = offer.contactClient as any;
    if (!contact) return;

    const phone = contact?.primaryContactPhone;
    const isMobile = phone?.phoneTypeId === "M";
    const isCompany = contact.contactType?.id === "C";

    const buyerFields: any = {
      buyerContactId: contact.id,
      buyerContactTypeId: contact.contactType?.id ?? null,
      buyerName: contact.name || contact.companyName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || null,
      buyerAddress: contact.primaryContactAddress
        ? `${contact.primaryContactAddress.line1 || ''} ${contact.primaryContactAddress.line2 || ''}, ${contact.primaryContactAddress.postalCode || ''} ${contact.primaryContactAddress.city || ''}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '')
        : null,
      buyerPersonalIdentificationNumber: contact.personalIdentificationNumber || contact.oib || null,
      buyerPhoneNumber: !isMobile ? phone?.phoneNumber || null : null,
      buyerMobilePhoneNumber: isMobile ? phone?.phoneNumber || null : null,
      buyerEmail: contact.primaryContactEmail?.emailAddress || null,
      buyerAuthorizedRepresentativeName:
        (contact.firstName || contact.lastName)
          ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim()
          : null,
    };

    // Ugovor otvoren izvan vozila (globalna pretraga) nema vozilo — preuzima se s ponude,
    // inače ostaje prazno i spremanje ide bez vozila.
    if (offer.vehicle?.id) buyerFields.vehicleId = offer.vehicle.id;

    // For companies, fetch representative data for phone, email, and name
    if (isCompany && contact.id) {
      try {
        const relations = await getContactRelationsAPI(contact.id);
        if (relations.length > 0) {
          const rep = relations[0];
          const repName = `${rep.personContact?.firstName || ''} ${rep.personContact?.lastName || ''}`.trim();
          if (repName) buyerFields.buyerAuthorizedRepresentativeName = repName;
          const repEmail = rep.personContact?.primaryContactEmail?.emailAddress;
          if (repEmail) buyerFields.buyerEmail = repEmail;

          // Fetch all phones to get both fixed and mobile
          if (rep.personContact?.id) {
            const phones = await getContactPhonesAPI(initialDataWithPagination.pagination, rep.personContact.id);
            const fixedPhone = phones.data.find(p => p.phoneTypeId === "F");
            const mobilePhone = phones.data.find(p => p.phoneTypeId === "M");
            buyerFields.buyerPhoneNumber = fixedPhone?.phoneNumber || buyerFields.buyerPhoneNumber || null;
            buyerFields.buyerMobilePhoneNumber = mobilePhone?.phoneNumber || buyerFields.buyerMobilePhoneNumber || null;
          }
        }
      } catch {
        // continue with default values
      }
    }

    setFormValues(prev => ({
      ...prev,
      offerSale: offer,
      ...buyerFields,
    }));
  }, [setFormValues]);


  return (
    <>
      <div className="flex flex-col gap-3 mb-3">
        <div className="flex gap-4 ">

          <div className="flex-1">
            <div className="flex gap-3">
              <div className="flex-1">
                <FleetOfferSaleDropdown
                  name="offerSaleId"
                  vehicleId={vehicleId}
                  label={t("admin.fleet.vehicle.contract.add.offernumber")}
                  placeholder={t(
                    "admin.fleet.vehicle.contract.add.offernumber.placeholder"
                  )}
                  value={formValues.offerSaleId}
                  onChange={handleDropdownChange}
                  onOfferSelect={handleOfferSelect}
                  required
                  error={errors.offerSaleId}
                  disabled={!!formValues.id}
                  showLinkedToContract={false}
                  showCancelled={false}
                  autoSelectLast={!formValues.id}
                  onLoaded={onOfferSalesLoaded}
                />
              </div>
              <div className="flex-1">
                <TextField
                  type="text"
                  name="contractNumber"
                  label={t("admin.fleet.vehicle.contract.add.contractnumber")}
                  onChange={handleTextFieldChange}
                  value={formValues.contractNumber}
                  disabled
                  required
                  error={errors.contractNumber}
                />
              </div>
            </div>
          </div>


          <div className="flex-1">
            <VehicleDropdown
              name="vehicleId"
              label={t("admin.fleet.vehicle.contract.add.vehicle")}
              placeholder={t(
                "admin.fleet.vehicle.contract.add.vehicle.placeholder"
              )}
              value={vehicleId}
              onChange={handleDropdownChange}
              required
              error={errors.vehicleId}
              disabled
            />
          </div>

        </div>

        {formValues.offerSaleId && (
          <div>
            <div className="flex gap-4 border-b border-slate-200 pb-4 px-4 mb-3  -mx-4">
              <div className="flex-1">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <DatePicker
                      name="contractDate"
                      label={t(
                        "admin.fleet.vehicle.contract.add.contractdate"
                      )}
                      placeholder={t(
                        "admin.fleet.vehicle.contract.add.contractdate.placeholder"
                      )}
                      onChange={handleDateChange}
                      error={errors.contractDate}
                      value={formValues.contractDate}
                      width="full"
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <NumberField
                      label={t("admin.fleet.vehicle.contract.add.price.depositamount")}
                      name="depositAmount"
                      onChange={handleNumberFieldChange}
                      value={formValues.depositAmount}
                      error={errors.depositAmount}
                      decimals={2}
                      groupAppend="€"
                      required

                    />
                  </div>
                </div>

              </div>
              <div className="flex-1">

              </div>

            </div>
            <div className="flex gap-4 border-b border-slate-200 pb-4 px-4 mb-3 -mx-4">
              <ContractSellerCard
                formValues={formValues}
                setFormValues={setFormValues}
                errors={errors}
                setErrors={setErrors}
              />
              <ContractBuyerCard
                formValues={formValues}
                setFormValues={setFormValues}
                errors={errors}
                setErrors={setErrors}
              />
            </div>

            <div className="flex gap-4 border-b border-slate-200 pb-4 px-4 mb-3 -mx-4">
              <div className="flex-1">
                <div className="text-md font-medium text-slate-900  mb-1.5">Isporuka vozila</div>
                <div className="flex gap-3 ">
                  <div className="flex-1">
                    <NumberField
                      label={t("admin.fleet.vehicle.contract.add.price.deliverytime")}
                      name="deliveryTime"
                      onChange={handleNumberFieldChange}
                      value={formValues.deliveryTime}
                      error={errors.deliveryTime}
                      decimals={0}
                      min={1}
                      step={1}
                      groupAppend="dana"
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <TextField
                      type="text"
                      name="placeOfVehicleDelivery"
                      label={t("admin.fleet.vehicle.contract.add.price.placeofvehicledelivery")}
                      placeholder={t("admin.fleet.vehicle.contract.add.price.placeofvehicledelivery.placeholder")}
                      onChange={handleTextFieldChange}
                      value={formValues.placeOfVehicleDelivery}
                      error={errors.placeOfVehicleDelivery}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="text-md font-medium text-slate-900  mb-1.5">
                  Zamjena staro za novo
                </div>
                <p className="text-sm font-light text-slate-800 mb-3">Podaci uneseni u ovoj sekciji odnose se na rabljeno vozilo koje prodavatelj preuzima od kupca u okviru kupnje po sistemu „staro za novo“. Unesite "staro" vozilo koje kupujete klikom na vozila iz izbornika ili dodajte novo "staro" vozilo klikom na gumb "+".</p>
                <div className="flex gap-3 ">
                  <div className="flex-1">
                    <VehicleDropdown
                      name="tradeInVehicleId"
                      label={t("admin.fleet.vehicle.contract.add.vehicle")}
                      placeholder={t(
                        "admin.fleet.vehicle.contract.add.vehicle.placeholder"
                      )}
                      value={formValues.tradeInVehicleId}
                      onChange={handleDropdownChange}
                      error={errors.tradeInVehicleId}
                      direction="up"
                    />
                  </div>
                  <div className="flex-1">

                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default memo(VehicleContractSaleInfo);
